import React from "react";
import { ChevronDown, ChevronUp, Clock, User } from "lucide-react";

function DeskSection({ title, count, children, emptyHint, defaultOpen = true, collapsible = false, open, onToggle, testId }) {
  const isOpen = collapsible ? open : true;
  return (
    <section className="bg-[var(--surface)] rounded-xl border border-[var(--border)] overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--border)] bg-[var(--bg)]/50">
        <h2 className="font-semibold text-[var(--text)] flex items-center gap-2">
          {title}
          <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-[var(--gold)]/15 text-[var(--gold)] num-display">{count}</span>
        </h2>
        {collapsible && (
          <button type="button" onClick={onToggle} className="p-2 rounded-md hover:bg-[var(--surface)] text-[var(--text-muted)] min-h-[44px] min-w-[44px] flex items-center justify-center">
            {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        )}
      </div>
      {isOpen && (
        <div className="p-3 space-y-2">
          {count === 0 ? (
            <p className="text-sm text-[var(--text-muted)] text-center py-6">{emptyHint}</p>
          ) : children}
        </div>
      )}
    </section>
  );
}

export function DeskBookingCard({
  b,
  lang,
  displayCustomerName,
  staffName,
  itemsPreview,
  statusBadge,
  effectiveStatus,
  sessionLabel,
  liveMinutes,
  onOpen,
  showDate,
}) {
  const custName = displayCustomerName(b) || (lang === "ar" ? "بدون اسم" : "Walk-in");
  return (
    <button
      type="button"
      onClick={() => onOpen(b)}
      className="w-full text-start rounded-lg border border-[var(--border)] bg-[var(--bg)]/30 hover:border-[var(--gold)] hover:shadow-md transition p-4 min-h-[72px] active:scale-[0.99]"
      data-testid={`desk-card-${b.booking_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {showDate && b.date && (
              <span className="text-xs font-medium text-[var(--text-muted)] num-display">{b.date}</span>
            )}
            <span className="font-mono text-sm font-bold text-[var(--gold)]">{b.time_slot || "—"}</span>
            {statusBadge(effectiveStatus)}
            {sessionLabel && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-violet-700 text-white">{sessionLabel}</span>
            )}
          </div>
          <div className="text-lg font-bold text-[var(--text)] truncate mt-1">{custName}</div>
          {itemsPreview && (
            <div className="text-xs text-[var(--text-muted)] truncate mt-0.5">{itemsPreview}</div>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-[var(--text-muted)]">
            {staffName && (
              <span className="inline-flex items-center gap-1">
                <User size={12} />
                {staffName}
              </span>
            )}
            {b.customer_phone && (
              <span dir="ltr">{b.customer_phone}</span>
            )}
            {liveMinutes != null && (
              <span className="inline-flex items-center gap-1 font-mono">
                <Clock size={12} />
                {liveMinutes}
                {lang === "ar" ? "د" : "m"}
              </span>
            )}
          </div>
        </div>
        {b.total_price > 0 && (
          <div className="text-sm font-semibold gold-text shrink-0">{b.total_price}</div>
        )}
      </div>
    </button>
  );
}

export default function BookingsDeskView({
  lang,
  selectedDate,
  isToday,
  deskSearch,
  setDeskSearch,
  groups,
  laterExpanded,
  setLaterExpanded,
  displayCustomerName,
  staffNameById,
  statusBadge,
  effectiveBookingStatus,
  bookingSessionState,
  liveSessionMinutes,
  visitTick,
  t,
  onOpenBooking,
  searchMode,
  searchLoading,
}) {
  void visitTick;

  const showDateOnCard = searchMode || !isToday;

  const itemsPreview = (b) => (
    b.items?.length
      ? b.items.map((i) => (lang === "ar" ? i.name_ar : i.name_en)).join(" · ")
      : (lang === "ar" ? b.item_name_ar : b.item_name_en) || ""
  );

  const renderCard = (b) => {
    const sess = bookingSessionState(b);
    let sessionLabel = null;
    if (sess === "waiting") sessionLabel = t("waiting");
    if (sess === "in_service") sessionLabel = t("in_service");
    const live = liveSessionMinutes(b);
    return (
      <DeskBookingCard
        key={b.booking_id}
        b={b}
        lang={lang}
        displayCustomerName={displayCustomerName}
        staffName={staffNameById[b.employee_user_id]}
        itemsPreview={itemsPreview(b)}
        statusBadge={statusBadge}
        effectiveStatus={effectiveBookingStatus(b)}
        sessionLabel={sessionLabel}
        liveMinutes={live.total}
        onOpen={onOpenBooking}
        showDate={showDateOnCard}
      />
    );
  };

  const scheduledTitle = isToday
    ? (lang === "ar" ? "القادمة قريباً" : "Up next")
    : (lang === "ar" ? "مواعيد اليوم المحدد" : "Scheduled");

  return (
    <div className="space-y-4" data-testid="bookings-desk-view">
      <div className="relative">
        <input
          type="search"
          value={deskSearch}
          onChange={(e) => setDeskSearch(e.target.value)}
          placeholder={lang === "ar" ? "بحث بالاسم أو الهاتف (كل التواريخ)..." : "Search name or phone (all dates)..."}
          className="w-full input-dark py-3 ps-4 pe-4 text-base min-h-[48px]"
          data-testid="desk-search"
        />
        {searchLoading && (
          <p className="text-xs text-[var(--text-muted)] mt-1">{lang === "ar" ? "جاري البحث..." : "Searching…"}</p>
        )}
      </div>

      {searchMode && (
        <DeskSection
          title={lang === "ar" ? "نتائج البحث" : "Search results"}
          count={groups.all.length}
          emptyHint={lang === "ar" ? "لا توجد حجوزات مطابقة" : "No matching bookings"}
          testId="desk-section-search"
        >
          {groups.all.map(renderCard)}
        </DeskSection>
      )}

      {!searchMode && !isToday && (
        <p className="text-sm text-[var(--text-muted)]">
          {lang === "ar" ? `عرض حجوزات ${selectedDate}` : `Showing bookings for ${selectedDate}`}
        </p>
      )}

      {!searchMode && isToday && (
        <>
          <DeskSection
            title={lang === "ar" ? "قيد الخدمة" : "In service"}
            count={groups.inService.length}
            emptyHint={lang === "ar" ? "لا أحد قيد الخدمة الآن" : "No one in service right now"}
            testId="desk-section-in-service"
          >
            {groups.inService.map(renderCard)}
          </DeskSection>

          <DeskSection
            title={lang === "ar" ? "في الانتظار" : "Waiting"}
            count={groups.waiting.length}
            emptyHint={lang === "ar" ? "لا عملاء في الانتظار" : "No customers waiting"}
            testId="desk-section-waiting"
          >
            {groups.waiting.map(renderCard)}
          </DeskSection>

          <DeskSection
            title={scheduledTitle}
            count={groups.upNext.length}
            emptyHint={lang === "ar" ? "لا مواعيد قريبة" : "Nothing coming up soon"}
            testId="desk-section-up-next"
          >
            {groups.upNext.map(renderCard)}
          </DeskSection>

          <DeskSection
            title={lang === "ar" ? "لاحقاً اليوم" : "Later today"}
            count={groups.later.length}
            emptyHint={lang === "ar" ? "لا مزيد من الحجوزات اليوم" : "No more bookings today"}
            collapsible
            open={laterExpanded}
            onToggle={() => setLaterExpanded((v) => !v)}
            testId="desk-section-later"
          >
            {groups.later.map(renderCard)}
          </DeskSection>
        </>
      )}

      {!searchMode && !isToday && (
        <DeskSection
          title={scheduledTitle}
          count={groups.all.length}
          emptyHint={lang === "ar" ? "لا حجوزات" : "No bookings"}
          testId="desk-section-day"
        >
          {groups.all.map(renderCard)}
        </DeskSection>
      )}

      {searchMode && !searchLoading && groups.all.length === 0 && (
        <p className="text-center text-[var(--text-muted)] py-8" data-testid="desk-search-empty">
          {lang === "ar" ? "لا نتائج لهذا البحث" : "No matches for this search"}
        </p>
      )}
    </div>
  );
}
